import { createApp } from 'vue'
import App from '@/App.vue'
import '@fortawesome/fontawesome-free/css/all.css'
import router from '@/router/index.js'
import '@/assets/index.css'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import axios from 'axios'
import serverUrl, { assetsUrl } from '@/assets/BaseServerUrl.js'
import { reportNetworkError } from '@/services/serverHealth'
import { message, messageBox, loading } from '@/utils/ui-service'
import { isSecurityBlock, SECURITY_BLOCK_REGEX } from '@/utils/httpErrorUtils'
import eventBus from '@/utils/eventBus'

// 全局立即注入自定义字体，保证在首屏渲染前就建立好 @font-face 规则
function initGlobalFonts() {
  const styleId = 'global-dynamic-fonts';
  if (typeof document === 'undefined' || document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @font-face {
      font-family: '猫啃网糖圆体';
      src: local('猫啃网糖圆体'), local('MaokenTangyuan'),
           url('${assetsUrl}/4yshop/font/猫啃网糖圆体.woff2') format('woff2'),
           url('${assetsUrl}/4yshop/font/猫啃网糖圆体.woff') format('woff'),
           url('${assetsUrl}/4yshop/font/猫啃网糖圆体.ttf') format('truetype');
      font-weight: 100 900;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: '爱点风雅黑';
      src: local('爱点风雅黑'), local('AidianFengYahei'),
           url('${assetsUrl}/4yshop/font/爱点风雅黑.woff2') format('woff2'),
           url('${assetsUrl}/4yshop/font/爱点风雅黑.woff') format('woff'),
           url('${assetsUrl}/4yshop/font/爱点风雅黑.ttf') format('truetype');
      font-weight: 100 900;
      font-style: normal;
      font-display: swap;
    }
  `;
  document.head.appendChild(style);
}
initGlobalFonts();

function printConsoleBanner() {
  try {
    const styleLarge = 'font-size:48px;font-weight:900;color:transparent;-webkit-text-stroke:2px #409EFF;letter-spacing:2px;'
    const styleSmall = 'font-size:24px;font-weight:700;color:transparent;-webkit-text-stroke:1.5px #67C23A;letter-spacing:1px;'
    console.log('%c云语匀雨', styleLarge)
    console.log('%c4yshop.top', styleSmall)
  } catch (e) {
    console.log('云语匀雨')
    console.log('4yshop.top')
  }
}

function setupDevtoolsDetection() {
  const TIP_TEXT = '科技是用来造福人类的，希望我们能站在同一战线而不是互相残杀'
  const THRESHOLD = 120
  let devtoolsWasOpen = false
  let lastShownAt = 0
  let baseWDiff = null
  let baseHDiff = null
  const showTipOnce = () => {
    const now = Date.now()
    // 基本冷却：避免短时间重复触发（例如连续快捷键）
    if (now - lastShownAt < 2500) return
    lastShownAt = now
    try {
      message.warning(TIP_TEXT, 4000)
    } catch (e) {
      // ElMessage 不可用时降级为 alert
      alert(TIP_TEXT)
    }
  }

  const isDevtoolsOpen = () => {
    const dw = (window.outerWidth - window.innerWidth)
    const dh = (window.outerHeight - window.innerHeight)
    if (baseWDiff === null || baseHDiff === null) {
      baseWDiff = dw
      baseHDiff = dh
      return false
    }
    const w = (dw - baseWDiff) > THRESHOLD
    const h = (dh - baseHDiff) > THRESHOLD
    const fb = (window.Firebug && window.Firebug.chrome && window.Firebug.chrome.isInitialized) || false
    return w || h || fb
  }

  // 定时检测：仅在从“关闭→打开”的瞬间提示一次
  if (typeof window !== 'undefined') {
    devtoolsWasOpen = isDevtoolsOpen()
    setInterval(() => {
      const open = isDevtoolsOpen()
      if (open && !devtoolsWasOpen) {
        devtoolsWasOpen = true
        showTipOnce()
      } else if (!open && devtoolsWasOpen) {
        devtoolsWasOpen = false
      }
    }, 1200)
    // 常见快捷键尝试打开控制台时提示
    document.addEventListener('keydown', (e) => {
      const key = String(e.key || '').toUpperCase()
      if (key === 'F12' || (e.ctrlKey && e.shiftKey && (key === 'I' || key === 'J' || key === 'C'))) {
        // 快捷键触发时也提示，但受冷却保护
        showTipOnce()
      }
    }, { passive: true })
  }
}

// 全局：请求过于频繁提示（429 或返回文案包含“频繁/Too Many Requests”）
const RATE_LIMIT_MSG = '请求过于频繁，请稍后重试'
const RATE_LIMIT_REGEX = /请求过于频繁|频繁|too\s*many\s*requests|rate\s*limit/i
let lastRateTipTs = 0
function showRateLimitWarningOnce() {
  const now = Date.now()
  if (now - lastRateTipTs < 800) return // 避免短时间内重复弹出
  lastRateTipTs = now
  message.warning(RATE_LIMIT_MSG)
}

const AUTH_ERROR_MSG = {
  401: '登录已过期，请重新登录',
  403: '权限不足'
}
let lastAuthTipTs = 0
let authRedirecting = false

function isAuthExpiredError(status, data) {
  if (status !== 401 && status !== 403) return false
  if (status === 401) return true
  const msg = String(data?.error || data?.message || '').toLowerCase()
  return msg.includes('令牌无效') || msg.includes('token') || msg.includes('身份验证') || msg.includes('访问被拒绝')
}

function handleAuthError(status, data) {
  const now = Date.now()
  if (now - lastAuthTipTs < 3000) return
  lastAuthTipTs = now

  const msg = data?.error || data?.message || AUTH_ERROR_MSG[status] || '请求失败'
  message.error(msg, 3000)

  if (status === 401 && !authRedirecting) {
    authRedirecting = true
    localStorage.removeItem('userToken')
    localStorage.removeItem('userInfo')
    localStorage.removeItem('lastLoginTime')
    localStorage.removeItem('isLoggedIn')
    // 通知全局：token 失效，即时重置用户态（如自定义壁纸、头部登录按钮）
    try { eventBus.emit('auth-cleared') } catch (e) { void e }
    setTimeout(() => {
      authRedirecting = false
      if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
        router.push('/')
      }
    }, 1500)
  }
}

// axios 响应拦截：统一处理认证和权限错误
axios.interceptors.response.use(
  (resp) => resp,
  (error) => {
    if (!error.response) {
      const url = error.config?.url || ''
      const isInternal = !url.startsWith('http') || url.startsWith(serverUrl)
      if (isInternal) {
        reportNetworkError()
      }
    }

    const status = error?.response?.status
    const data = error?.response?.data || {}
    const msg = String(data?.message || data?.error || '')

    // 安全拦截检测（优先于其他403处理）
    if (isSecurityBlock(error)) {
      eventBus.emit('security-blocked')
      return Promise.reject(error)
    }

    if (status === 429 || RATE_LIMIT_REGEX.test(msg)) {
      showRateLimitWarningOnce()
    } else if (isAuthExpiredError(status, data)) {
      handleAuthError(status, data)
    }

    return Promise.reject(error)
  }
)

// 极简纯 JS md5 函数，用于防刷接口签名校验
function simpleMd5(string) {
  function RotateLeft(lValue, iShiftBits) {
    return (lValue<<iShiftBits) | (lValue>>>(32-iShiftBits));
  }
  function AddUnsigned(lX,lY) {
    var lX4,lY4,lX8,lY8,lResult;
    lX8 = (lX & 0x80000000);
    lY8 = (lY & 0x80000000);
    lX4 = (lX & 0x40000000);
    lY4 = (lY & 0x40000000);
    lResult = (lX & 0x3FFFFFFF)+(lY & 0x3FFFFFFF);
    if (lX4 & lY4) {
      return (lResult ^ 0x80000000 ^ lX8 ^ lY8);
    }
    if (lX4 | lY4) {
      if (lResult & 0x40000000) {
        return (lResult ^ 0xC0000000 ^ lX8 ^ lY8);
      } else {
        return (lResult ^ 0x40000000 ^ lX8 ^ lY8);
      }
    } else {
      return (lResult ^ lX8 ^ lY8);
    }
  }
  function F(x,y,z) { return (x & y) | ((~x) & z); }
  function G(x,y,z) { return (x & z) | (y & (~z)); }
  function H(x,y,z) { return (x ^ y ^ z); }
  function I(x,y,z) { return (y ^ (x | (~z))); }
  function II(a,b,c,d,x,s,ac) {
    a = AddUnsigned(a, AddUnsigned(AddUnsigned(F(b,c,d), x), ac));
    return AddUnsigned(RotateLeft(a, s), b);
  }
  function GG(a,b,c,d,x,s,ac) {
    a = AddUnsigned(a, AddUnsigned(AddUnsigned(G(b,c,d), x), ac));
    return AddUnsigned(RotateLeft(a, s), b);
  }
  function HH(a,b,c,d,x,s,ac) {
    a = AddUnsigned(a, AddUnsigned(AddUnsigned(H(b,c,d), x), ac));
    return AddUnsigned(RotateLeft(a, s), b);
  }
  function II2(a,b,c,d,x,s,ac) {
    a = AddUnsigned(a, AddUnsigned(AddUnsigned(I(b,c,d), x), ac));
    return AddUnsigned(RotateLeft(a, s), b);
  }
  function ConvertToWordArray(string) {
    var lWordCount;
    var lMessageLength = string.length;
    var lNumberOfWords_temp1=lMessageLength + 8;
    var lNumberOfWords_temp2=(lNumberOfWords_temp1 - (lNumberOfWords_temp1 % 64))/64;
    var lNumberOfWords = (lNumberOfWords_temp2 + 1)*16;
    var lWordArray=Array(lNumberOfWords-1);
    var lBytePosition = 0;
    var lByteCount = 0;
    while ( lByteCount < lMessageLength ) {
      lWordCount = (lByteCount - (lByteCount % 4))/4;
      lBytePosition = (lByteCount % 4)*8;
      lWordArray[lWordCount] = (lWordArray[lWordCount] | (string.charCodeAt(lByteCount)<<lBytePosition));
      lByteCount++;
    }
    lWordCount = (lByteCount - (lByteCount % 4))/4;
    lBytePosition = (lByteCount % 4)*8;
    lWordArray[lWordCount] = lWordArray[lWordCount] | (0x80<<lBytePosition);
    lWordArray[lNumberOfWords-2] = lMessageLength<<3;
    lWordArray[lNumberOfWords-1] = lMessageLength>>>29;
    return lWordArray;
  }
  function WordToHex(lValue) {
    var WordToHexValue="",WordToHexValue_temp="",lByte,lCount;
    for (lCount = 0;lCount<=3;lCount++) {
      lByte = (lValue>>>(lCount*8)) & 255;
      WordToHexValue_temp = "0" + lByte.toString(16);
      WordToHexValue = WordToHexValue + WordToHexValue_temp.substr(WordToHexValue_temp.length-2,2);
    }
    return WordToHexValue;
  }
  function Utf8Encode(string) {
    string = string.replace(/\r\n/g,"\n");
    var utftext = "";
    for (var n = 0; n < string.length; n++) {
      var c = string.charCodeAt(n);
      if (c < 128) {
        utftext += String.fromCharCode(c);
      } else if((c > 127) && (c < 2048)) {
        utftext += String.fromCharCode((c >> 6) | 192);
        utftext += String.fromCharCode((c & 63) | 128);
      } else {
        utftext += String.fromCharCode((c >> 12) | 224);
        utftext += String.fromCharCode(((c >> 6) & 63) | 128);
        utftext += String.fromCharCode((c & 63) | 128);
      }
    }
    return utftext;
  }
  var x=Array();
  var k,AA,BB,CC,DD,a,b,c,d;
  var S11=7, S12=12, S13=17, S14=22;
  var S21=5, S22=9 , S23=14, S24=20;
  var S31=4, S32=11, S33=16, S34=23;
  var S41=6, S42=10, S43=15, S44=21;
  string = Utf8Encode(string);
  x = ConvertToWordArray(string);
  a = 0x67452301; b = 0xEFCDAB89; c = 0x98BADCFE; d = 0x10325476;
  for (k=0;k<x.length;k+=16) {
    AA=a; BB=b; CC=c; DD=d;
    a=II(a,b,c,d,x[k+0], S11,0xD76AA478); d=II(d,a,b,c,x[k+1], S12,0xE8C7B756); c=II(c,d,a,b,x[k+2], S13,0x242070DB); b=II(b,c,d,a,x[k+3], S14,0xC1BDCEEE);
    a=II(a,b,c,d,x[k+4], S11,0xF57C0FAF); d=II(d,a,b,c,x[k+5], S12,0x4787C62A); c=II(c,d,a,b,x[k+6], S13,0xA8304613); b=II(b,c,d,a,x[k+7], S14,0xFD469501);
    a=II(a,b,c,d,x[k+8], S11,0x698098D8); d=II(d,a,b,c,x[k+9], S12,0x8B44F7AF); c=II(c,d,a,b,x[k+10],S13,0xFFFF5BB1); b=II(b,c,d,a,x[k+11],S14,0x895CD7BE);
    a=II(a,b,c,d,x[k+12],S11,0x6B901122); d=II(d,a,b,c,x[k+13],S12,0xFD987193); c=II(c,d,a,b,x[k+14],S13,0xA679438E); b=II(b,c,d,a,x[k+15],S14,0x49B40821);
    a=GG(a,b,c,d,x[k+1], S21,0xF61E2562); d=GG(d,a,b,c,x[k+6], S22,0xC040B340); c=GG(c,d,a,b,x[k+11],S23,0x265E5A51); b=GG(b,c,d,a,x[k+0], S24,0xE9B6C7AA);
    a=GG(a,b,c,d,x[k+5], S21,0xD62F105D); d=GG(d,a,b,c,x[k+10],S22,0x2441453);  c=GG(c,d,a,b,x[k+15],S23,0xD8A1E681); b=GG(b,c,d,a,x[k+4], S24,0xE7D3FBC8);
    a=GG(a,b,c,d,x[k+9], S21,0x21E1CDE6); d=GG(d,a,b,c,x[k+14],S22,0xC33707D6); c=GG(c,d,a,b,x[k+3], S23,0xF4D50D87); b=GG(b,c,d,a,x[k+8], S24,0x455A14ED);
    a=GG(a,b,c,d,x[k+13],S21,0xA9E3E905); d=GG(d,a,b,c,x[k+2], S22,0xFCEFA3F8); c=GG(c,d,a,b,x[k+7], S23,0x676F02D9); b=GG(b,c,d,a,x[k+12],S24,0x8D2A4C8A);
    a=HH(a,b,c,d,x[k+5], S31,0xFFFA3942); d=HH(d,a,b,c,x[k+8], S32,0x8771F681); c=HH(c,d,a,b,x[k+11],S33,0x6D9D6122); b=HH(b,c,d,a,x[k+14],S34,0xFDE5380C);
    a=HH(a,b,c,d,x[k+1], S31,0xA4BEEA44); d=HH(d,a,b,c,x[k+4], S32,0x4BDECFA9); c=HH(c,d,a,b,x[k+7], S33,0xF6BB4B60); b=HH(b,c,d,a,x[k+10],S34,0xBEBFBC70);
    a=HH(a,b,c,d,x[k+13],S31,0x289B7EC6); d=HH(d,a,b,c,x[k+0], S32,0xEAA127FA); c=HH(c,d,a,b,x[k+3], S33,0xD4EF3085); b=HH(b,c,d,a,x[k+6], S34,0x04881D05);
    a=HH(a,b,c,d,x[k+9], S31,0xD9D4D039); d=HH(d,a,b,c,x[k+12],S32,0xE6DB99E5); c=HH(c,d,a,b,x[k+15],S33,0x1FA27CF8); b=HH(b,c,d,a,x[k+2], S34,0xC4AC5665);
    a=II2(a,b,c,d,x[k+0], S41,0xF4292244); d=II2(d,a,b,c,x[k+7], S42,0x432AFF97); c=II2(c,d,a,b,x[k+14],S43,0xAB9423A7); b=II2(b,c,d,a,x[k+5], S44,0xFC93A039);
    a=II2(a,b,c,d,x[k+12],S41,0x655B59C3); d=II2(d,a,b,c,x[k+3], S42,0x8F0CCC92); c=II2(c,d,a,b,x[k+10],S43,0xFFEFF47D); b=II2(b,c,d,a,x[k+1], S44,0x85845DD1);
    a=II2(a,b,c,d,x[k+8], S41,0x6FA87E4F); d=II2(d,a,b,c,x[k+15],S42,0xFE2CE6E0); c=II2(c,d,a,b,x[k+6], S43,0xA3014314); b=II2(b,c,d,a,x[k+13],S44,0x4E0811A1);
    a=II2(a,b,c,d,x[k+4], S41,0xF7537E82); d=II2(d,a,b,c,x[k+11],S42,0xBD3AF235); c=II2(c,d,a,b,x[k+2], S43,0x2AD7D2BB); b=II2(b,c,d,a,x[k+9], S44,0xEB86D391);
    a=AddUnsigned(a,AA); b=AddUnsigned(b,BB); c=AddUnsigned(c,CC); d=AddUnsigned(d,DD);
  }
  var temp = WordToHex(a)+WordToHex(b)+WordToHex(c)+WordToHex(d);
  return temp.toLowerCase();
}

// 与后端 securityConfig.signSalt 对应的签名盐值
const SIGN_SALT = '4yshop_ai_secure_salt_2026';

// 从请求 URL 中取出裸路径（去掉协议域名与查询串），用于生成接口签名
function extractApiPath(url) {
  if (!url || typeof url !== 'string') return '';
  let path = url;
  const originMatch = path.match(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i);
  if (originMatch) path = path.slice(originMatch[0].length);
  path = path.split('?')[0].split('#')[0];
  if (!path.startsWith('/')) path = `/${path}`;
  return path;
}

// axios 请求拦截：自动附加认证头
axios.interceptors.request.use(
  (config) => {
    // 只有发往本站后端的请求才携带 Token
    const isInternalRequest = !config.url.startsWith('http') || config.url.startsWith(serverUrl);
    
    if (isInternalRequest) {
      config.headers['X-Requested-With'] = 'XMLHttpRequest'
      const userToken = localStorage.getItem('userToken')
      if (userToken) {
        config.headers['Authorization'] = `Bearer ${userToken}`
      }

      // 安全防御：为签到与翻译接口动态生成签名头，防恶意脚本私自调用
      // 服务端按 req.path 校验签名，因此必须用请求的真实路径去签名
      const SIGNED_PATHS = ['/api/user/checkin', '/api/translate/async'];
      const signedPath = SIGNED_PATHS.find(p => extractApiPath(config.url) === p);
      if (signedPath) {
        const timestamp = Date.now().toString();
        const nonce = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

        const signature = simpleMd5(signedPath + timestamp + nonce + SIGN_SALT);

        config.headers['x-sign-timestamp'] = timestamp;
        config.headers['x-sign-nonce'] = nonce;
        config.headers['x-sign-signature'] = signature;
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// fetch 也做限流提示和认证错误处理（前端部分接口使用了 fetch）
if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
  const originalFetch = window.fetch.bind(window)
  window.fetch = async (...args) => {
    try {
      const res = await originalFetch(...args)
      try {
        if (!res.ok) {
          if (res.status === 429) {
            showRateLimitWarningOnce()
          } else if (res.status === 403) {
            let data = {}
            try { data = await res.clone().json() } catch (e) {
              try { data = { error: await res.clone().text() } } catch (e2) { /* ignore */ }
            }
            const msg = String(data?.message || data?.error || '')
            if (SECURITY_BLOCK_REGEX.test(msg)) {
              eventBus.emit('security-blocked')
            } else if (isAuthExpiredError(res.status, data)) {
              handleAuthError(res.status, data)
            }
          } else if (res.status === 401) {
            let data = {}
            try { data = await res.clone().json() } catch (e) {
              try { data = { error: await res.clone().text() } } catch (e2) { /* ignore */ }
            }
            if (isAuthExpiredError(res.status, data)) {
              handleAuthError(res.status, data)
            }
          } else {
            let text = ''
            try { text = await res.clone().text() } catch (err) {
              console.error('读取响应文本失败:', err)
            }
            if (RATE_LIMIT_REGEX.test(text)) showRateLimitWarningOnce()
          }
        }
      } catch { /* 忽略读取失败 */ }
      return res
    } catch (e) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || ''
      const isInternal = !url.startsWith('http') || url.startsWith(serverUrl)
      if (isInternal) {
        reportNetworkError()
      }
      throw e
    }
  }
}

const app = createApp(App)
app.use(router)
app.use(ElementPlus)

// Override Element Plus global properties with our custom UI service
app.config.globalProperties.$message = message
app.config.globalProperties.$messageBox = messageBox
app.config.globalProperties.$confirm = messageBox.confirm
app.config.globalProperties.$alert = messageBox.alert
app.config.globalProperties.$prompt = messageBox.prompt
app.config.globalProperties.$loading = loading

app.mount('#app')

// 启动后打印控制台空心字，并启用开发者工具打开提示
printConsoleBanner()
setupDevtoolsDetection()
